import { Box, Container, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { VITAL_CHART } from '../../shared/Strings';

const legendContainer = { width: '100%', pb: '12px', pl: '55px' };
const tooltipContainer = {
  background: '#000000',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  color: '#ffffff',
  lineHeight: 0,
  boxShadow: '2px 2px 16px rgba(75, 75, 75, 0.12)',
  borderRadius: 8,
};

// this implementation fetches the min of systolic/diastolic and max of systolic/diastolic
const getBpLimits = data => {
  const systolicRangeObj = {
    min:
      data.length === 1
        ? data[0].value.high
        : data[0].value && data[0].value.high && !isNaN(data[0].value.high)
        ? data[0].value.high
        : Number.MAX_VALUE,
    max:
      data.length === 1
        ? data[0].value.high
        : data[0].value && data[0].value.high && !isNaN(data[0].value.high)
        ? data[0].value.high
        : Number.MIN_VALUE,
  };
  const diastolicRangeObj = {
    min:
      data.length === 1
        ? data[0].value.low
        : data[0].value && data[0].value.low && !isNaN(data[0].value.low)
        ? data[0].value.low
        : Number.MAX_VALUE,
    max:
      data.length === 1
        ? data[0].value.low
        : data[0].value && data[0].value.low && !isNaN(data[0].value.low)
        ? data[0].value.low
        : Number.MIN_VALUE,
  };

  data.forEach(obj => {
    if (obj.value && obj.value.high && !isNaN(obj.value.high)) {
      systolicRangeObj.min =
        obj.value.high < systolicRangeObj.min
          ? obj.value.high
          : systolicRangeObj.min;
      systolicRangeObj.max =
        obj.value.high > systolicRangeObj.max
          ? obj.value.high
          : systolicRangeObj.max;
    }

    if (obj.value && obj.value.low && !isNaN(obj.value.low)) {
      diastolicRangeObj.min =
        obj.value.low < diastolicRangeObj.min
          ? obj.value.low
          : diastolicRangeObj.min;
      diastolicRangeObj.max =
        obj.value.low > diastolicRangeObj.max
          ? obj.value.low
          : diastolicRangeObj.max;
    }
  });
  return {
    min: `${systolicRangeObj.min}-${systolicRangeObj.max}`,
    max: `${diastolicRangeObj.min}-${diastolicRangeObj.max}`,
  };
};

const getMinMax = data => {
  let minVal = data[0].value;
  let maxVal = data[0].value;
  data.forEach(obj => {
    minVal = obj.value < minVal ? obj.value : minVal;
    maxVal = obj.value > maxVal ? obj.value : maxVal;
  });
  return { min: minVal, max: maxVal };
};

const CustomTooltip = ({ active, payload, isBpGraph }) => {
  if (payload.length && active && payload[0].payload) {
    return (
      <Box sx={tooltipContainer}>
        <Typography variant="columnName">{payload[0].payload.date}</Typography>
        <Typography variant="nav" sx={{ p: '5px' }}>
          {!isBpGraph
            ? payload[0].payload.value
            : `${payload[0].payload.value.high}/${payload[0].payload.value.low}`}{' '}
          <Typography
            display="inline"
            variant="bannerMobile"
            sx={{ color: 'e4e4e4' }}>
            {payload[0].payload.unit}
          </Typography>
        </Typography>
      </Box>
    );
  }
  return 'null';
};

export const VitalChart = ({
  dateFilterText,
  allData = [],
  filteredData = [],
  isBpGraph,
}) => {
  const [minVal, setMinVal] = useState(null);
  const [maxVal, setMaxVal] = useState(null);

  useEffect(() => {
    if (filteredData.length) {
      const { min, max } = isBpGraph
        ? getBpLimits(filteredData)
        : getMinMax(filteredData);
      setMinVal(min);
      setMaxVal(max);
    }
  }, [filteredData, isBpGraph]);

  return (
    <>
      <Container sx={legendContainer}>
        <Typography variant="h6">{VITAL_CHART.RANGE}</Typography>
        <Typography variant="h3">
          {minVal && maxVal
            ? isBpGraph
              ? `${minVal}/${maxVal}`
              : `${minVal}-${maxVal}`
            : minVal
            ? `${minVal}`
            : ''}
          <Typography display="inline" variant="bannerMobile">
            {allData && allData[allData.length - 1].unit}
          </Typography>
        </Typography>
        <Typography variant="h6">{dateFilterText}</Typography>
      </Container>
      <Container sx={{ width: '100%', height: 300 }}>
        <ResponsiveContainer height={290}>
          <LineChart data={filteredData}>
            <CartesianGrid strokeDasharray="1 1" />
            <XAxis
              dataKey={allData && 'date'}
              angle={-30}
              dy={25}
              height={60}
              style={{ fontSize: 14, fontFamily: 'Inter', fontWeight: 600 }}
            />
            <YAxis tickCount={5} domain={[20, 100]} />
            <Tooltip content={<CustomTooltip isBpGraph={isBpGraph} />} />
            {isBpGraph ? (
              <>
                <Line
                  type="monotone"
                  dataKey="value.low"
                  strokeWidth={2}
                  stroke="blue"
                  fill="blue"
                />
                <Line
                  type="monotone"
                  dataKey="value.high"
                  strokeWidth={2}
                  stroke="#DB0032"
                  fill="#DB0032"
                />
              </>
            ) : (
              <Line
                type="monotone"
                dataKey="value"
                strokeWidth={2}
                stroke="#DB0032"
                fill="#DB0032"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Container>
    </>
  );
};
